import { Text } from '@chakra-ui/react'
import type { MemberMe } from '@ipc/shared'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router'
import { isApiError } from '~/api/client'
import { useMe } from '~/api/hooks'
import { RetryError } from '~/components/member/feedback'
import { useT } from '~/i18n/use-t'
import { StepSkeleton } from '~/components/member/StepSkeleton'

type Me = MemberMe & { member: NonNullable<MemberMe['member']> }

/**
 * The guard of every `/me/*` section page: loading and error states as before, 401 goes to the login, and a person
 * without a `member` (a "not accepted" or "do the face check again" card) goes back to the Overview, which shows the card.
 */
export function MemberSectionGuard({ children }: { children: (me: Me) => ReactNode }) {
  const { t } = useT()
  const me = useMe()
  if (me.isPending) return <StepSkeleton />
  if (me.isError) {
    if (isApiError(me.error) && me.error.status === 401) return <Navigate to='/login' replace />
    return <RetryError error={me.error} onRetry={() => void me.refetch()} />
  }
  if (!me.data.member) return <Navigate to='/me' replace />
  return <>{children(me.data as Me)}</>
}
